/**
 * Helper functions for determining song date-based badges
 */

import i18n from '../i18n';

const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const ONE_MONTH_IN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

/**
 * Get relative time string (e.g., "1d ago", "3w ago", "2m ago")
 * @param {string} dateString - ISO date string
 * @returns {string} - Relative time string
 */
export const getRelativeTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  const ago = (unit, count) => i18n.t('time.ago', { time: i18n.t(`time.short.${unit}`, { count }) });

  if (months > 0) return ago('months', months);
  if (weeks > 0) return ago('weeks', weeks);
  if (days > 0) return ago('days', days);
  if (hours > 0) return ago('hours', hours);
  if (minutes > 0) return ago('minutes', minutes);
  return i18n.t('time.justNow');
};

/**
 * Get detailed relative time string with years and months (e.g., "1 year and 3 months ago", "2 years ago")
 * @param {string} dateString - ISO date string
 * @returns {string} - Detailed relative time string
 */
export const getDetailedRelativeTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;

  // If date is in the future, don't display the time
  if (diffMs < 0) return '';

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(days / 7);
  const totalMonths = Math.floor(days / 30);
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  const span = (unit, count) => i18n.t(`time.${unit}`, { count });
  const ago = (time) => i18n.t('time.ago', { time });

  if (years > 0) {
    if (months > 0) return ago(i18n.t('time.and', { first: span('years', years), second: span('months', months) }));
    return ago(span('years', years));
  }
  if (totalMonths > 0) return ago(span('months', totalMonths));
  if (weeks > 0) return ago(span('weeks', weeks));
  if (days > 0) return ago(span('days', days));
  if (hours > 0) return ago(span('hours', hours));
  if (minutes > 0) return ago(span('minutes', minutes));
  return i18n.t('time.justNow');
};

/**
 * Check if a song should be treated as new.
 * Returns true if manually flagged as new (isNew) OR created within the last month.
 * @param {string} createdAt - ISO date string when the song was created
 * @param {boolean} [isNew=false] - Manual override flag set by the user
 * @returns {boolean}
 */
export const isNewSong = (createdAt, isNew = false) => {
  if (isNew) return true;
  if (!createdAt) return false;

  const created = new Date(createdAt);
  const now = new Date();
  const timeDiff = now - created;

  return timeDiff <= ONE_MONTH_IN_MS;
};

/**
 * Check if a song has been edited since it was created, whenever that happened.
 * A song saved once and never touched again carries an updatedAt that only echoes its
 * creation, so anything sorting or badging on "was this edited" has to discount that.
 * @param {string} createdAt - ISO date string when the song was created
 * @param {string} updatedAt - ISO date string when the song was last updated
 * @returns {boolean} - True if the update is a real edit rather than the original save
 */
export const wasContentUpdated = (createdAt, updatedAt) => {
  if (!createdAt || !updatedAt) return false;

  const created = new Date(createdAt);
  const updated = new Date(updatedAt);

  return Math.abs(updated - created) > 1000; // More than 1 second difference
};

/**
 * Check if a song was updated within the last week (and is different from creation date)
 * @param {string} createdAt - ISO date string when the song was created
 * @param {string} updatedAt - ISO date string when the song was last updated
 * @returns {boolean} - True if the song was recently updated (within the last week and different from creation)
 */
export const isRecentlyUpdated = (createdAt, updatedAt) => {
  if (!wasContentUpdated(createdAt, updatedAt)) return false;

  // Check if update was within the last week
  const timeDiffFromUpdate = new Date() - new Date(updatedAt);

  return timeDiffFromUpdate <= ONE_WEEK_IN_MS;
};
